import React, { Suspense, useCallback } from 'react';

import ModalTransition from '@atlaskit/modal-dialog/modal-transition';

import { PeopleAndTeamsConfigurationProvider } from '@atlassian/people-teams-configuration-client/main';
import type { Flag } from '@atlassian/people-teams/flag';

import { LoadableAfterPaint } from '@confluence/loadable';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { FlagDescriptor } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

const TEAM_CREATE_ANALYTICS_ATTRIBUTES = { ref: 'confluenceSideNavigation' };

const LazyTeamCreateDialog = LoadableAfterPaint({
	loader: () =>
		import(
			/* webpackChunkName: "loadable-TeamAppShortcutItemCreateTeamDialog" */ '@atlassian/people-teams'
		).then(({ TeamCreateDialog }) => TeamCreateDialog),
	loading: () => null,
});

type CreateTeamModalProps = {
	isOpen: boolean;
	onClose: () => void;
	addFlag: (flag: FlagDescriptor) => void;
};
export function CreateTeamModal({
	isOpen,
	onClose,
	addFlag,
}: CreateTeamModalProps): React.JSX.Element {
	const { cloudId, orgId } = useSessionData();

	const addFlagFn = useCallback(
		(flag: Flag) => {
			const { title, description, appearance, actions } = flag;
			addFlag({
				type: appearance === 'success' || appearance === 'normal' ? 'success-circle' : appearance,
				title,
				description,
				close: 'auto',
				actions,
				id: flag.id,
			});
		},
		[addFlag],
	);

	return (
		<ModalTransition>
			{isOpen ? (
				<Suspense>
					<PeopleAndTeamsConfigurationProvider product="confluence">
						<LazyTeamCreateDialog
							cloudId={cloudId}
							product="confluence"
							orgId={orgId}
							extraAnalyticsAttrs={TEAM_CREATE_ANALYTICS_ATTRIBUTES}
							onClose={onClose}
							enableMembershipSettingsChoice
							enableTeamPermissionCheck
							onCreateTeamSuccess={onClose}
							onCreateTeamFailed={onClose}
							addFlag={addFlagFn}
						/>
					</PeopleAndTeamsConfigurationProvider>
				</Suspense>
			) : null}
		</ModalTransition>
	);
}
